import React, { useState } from 'react';
import {
    DialogContent,
    Grid,
    Typography,
    Chip,
    Box,
    InputBase,
    IconButton,
    Avatar,
    Skeleton,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SendIcon from '@mui/icons-material/Send';
import { PremiumColor } from '../../../core-components/theme/color-palette/color-palette.theme';
import AIStarsIcon from '../../../core-components/assets/icons/AIStarsIcon';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import { useInsightComments } from '../hooks/useInsightCommentDetails';
import { useInsightDescription } from '../hooks/useGenerateInsightDescription';
import { Neutrals, Premium, Primary } from '../../../core-components/theme/color-palette/colors';
import { usePostInsightComment } from '../hooks/usePostInsightComment';
import { InsightDetailsContentProps } from '../types/InsightsType';
import { formatInsightDateTime } from '../helpers/insightsHelper';
import '../styles/insightDetails.css'

const InsightDetailsContent: React.FC<InsightDetailsContentProps> = ({ insightId, insightDetails, isPublic = false }) => {


    const [newComment, setNewComment] = useState('');
    const { postComment } = usePostInsightComment(insightId);




    const { comments, lastElementRef, commentContainerRef } = useInsightComments(insightId, isPublic);
    const { description, isFetching, error, refetchDescription } = useInsightDescription(insightId);

    const handleCommentSubmit = () => {
        if (!newComment.trim()) return;
        postComment(newComment);
        setNewComment('');
    };


    return (
        <DialogContent sx={{ paddingLeft: '21px', pr: 3, paddingTop: 1, flex: 1, paddingBottom: 0 }}>
            <Grid container spacing={3}>
                {insightDetails?.equipments && insightDetails?.equipments?.length > 0 && (
                    <Grid item xs={12} sx={{ px: '0 !important', marginTop: 2 }}>
                        <Typography variant="h6">Equipments</Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                            {insightDetails?.equipments?.map((equipment: any) => (
                                <Chip
                                    key={equipment}
                                    label={equipment}
                                    sx={{ backgroundColor: 'white', border: '1px solid #ccc', mr: 1, }}
                                />
                            ))}
                        </Box>
                    </Grid>
                )}
                <Grid item xs={12} mt={insightDetails?.equipments?.length > 0 ? 1 : 3} sx={{ p: '0 !important' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, padding: 1, justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AIStarsIcon width="14" height="14" startColor={Premium[500]} endColor={Premium[500]} />
                            <Typography variant="h6" sx={{ color: PremiumColor?.main }}>
                                Description
                            </Typography>
                        </Box>
                        <Typography
                            onClick={isPublic ? undefined : () => refetchDescription()}
                            sx={{
                                pl: 1,
                                color: Premium[500],
                                cursor: isPublic ? 'not-allowed' : 'pointer',
                                opacity: isPublic ? 0.5 : 1,
                                pointerEvents: isPublic ? 'none' : 'auto'
                            }}
                        >
                            <span title={isPublic ? "Login to refresh" : ""}>
                                <Box
                                    sx={{
                                        padding: 1,
                                        borderRadius: '50%',
                                        border: '1px solid',
                                        borderColor: Premium[700],
                                        backgroundColor: Premium[50],
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        opacity: isPublic ? 0.5 : 1,
                                    }}
                                >
                                    <AutorenewOutlinedIcon
                                        sx={{
                                            color: Premium[700],
                                            fontSize: 12,
                                            animation: isFetching ? 'spin 1s linear infinite' : 'none',
                                            '@keyframes spin': {
                                                '0%': { transform: 'rotate(0deg)' },
                                                '100%': { transform: 'rotate(360deg)' },
                                            },
                                        }}
                                    />
                                </Box>
                            </span>
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            position: 'relative',
                            borderRadius: '8px',
                            bgcolor: 'rgba(249, 246, 254, 1)',
                            overflow: 'hidden',
                            mt: 0,
                            ml: 0,
                            border: `2px solid ${Premium[200]}`,
                        }}
                    >
                        {((insightDetails?.description || description) && !isFetching) &&
                            <Box
                                className="animated-border-line"
                                sx={{
                                    position: 'absolute',
                                    background: Premium[700],
                                    boxShadow: `0 0 10px 3px ${Premium[800]}`,
                                    animation: 'walk-strip 5s linear infinite',
                                    zIndex: 2,
                                    width: '80px',
                                    height: '80px',
                                }}
                            />
                        }


                        <Box sx={{ zIndex: 3, position: 'relative', padding: '2px' }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: 16,
                                    p: 2,
                                    borderRadius: '6px',
                                    color: PremiumColor?.main,
                                    whiteSpace: 'pre-wrap',
                                    background: 'rgba(249, 246, 254, 1)',
                                }}
                            >
                                {isFetching ? (
                                    <Box sx={{ p: 2 }}>
                                        <Skeleton variant="text" height={24} sx={{ bgcolor: Premium[100], mb: 1 }} />
                                        <Skeleton variant="text" height={24} sx={{ bgcolor: Premium[100] }} />
                                    </Box>
                                ) : error ? (
                                    'Error loading description'
                                ) : (
                                    description || insightDetails?.description
                                )}
                            </Typography>
                        </Box>

                
                    </Box>

                </Grid>



                <Grid container mt={1} spacing={2} className="insightDetailsMain">
                    <Grid item xs={12} sm={6}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,

                            }}>
                            <Box>
                                <Typography variant="h6" sx={{ mb: 1 }}>
                                    Details
                                </Typography>
                                <Box
                                    sx={{
                                        borderRadius: 2,
                                        py: 1,
                                        overflow: 'auto',
                                        minHeight: insightDetails?.richTextContent ? '120px' : '23.4rem',
                                        maxHeight: insightDetails?.rcaContent
                                            ? {
                                                xs: '10rem',
                                                sm: '7.3rem',
                                                md: '7.5rem',
                                                lg: '7.5rem',
                                                xl: '7.5em',
                                            }
                                            : '23rem',

                                        border: '1px solid #ccc',
                                        scrollbarWidth: 'none',
                                        '&::-webkit-scrollbar': { width: '6px' },
                                        '&::-webkit-scrollbar-thumb': {
                                            backgroundColor: '#ccc',
                                            borderRadius: '3px',
                                        },
                                    }}
                                >
                                    {insightDetails?.richTextContent ? (
                                        <div
                                            dangerouslySetInnerHTML={{ __html: insightDetails.richTextContent }}
                                            className="ql-editor"
                                            style={{
                                                width: '100%',
                                                overflowWrap: 'break-word',
                                                scrollbarWidth: 'none',
                                            }}
                                        />
                                    ) : (
                                        <Typography variant="body2" color="text.secondary" sx={{marginLeft:"10px", marginTop:"10px"}}>
                                            No Details Available for Insight
                                        </Typography>
                                    )}
                                </Box>
                            </Box>

                            {insightDetails?.rcaContent && (
                                <Box sx={{ marginTop: 1 }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            gap: 1,
                                            flexWrap: 'wrap',
                                            alignItems: 'center',
                                            mb: 0,

                                        }}
                                    >
                                        <AIStarsIcon width="14" height="14" startColor={Premium[500]} endColor={Premium[500]} />
                                        <Typography variant="h6" sx={{ color: PremiumColor?.main }}>
                                            RCA
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            borderRadius: '6px',
                                            overflow: 'hidden',
                                            width: '100%',
                                            color: 'white',
                                            border: `2px solid ${Premium[200]}`,
                                            mt: 1,
                                            backgroundColor: 'rgba(242, 237, 252, 0.5)'

                                        }}
                                    >
                                        <Box
                                            className="animated-border-line"
                                            sx={{
                                                position: 'absolute',
                                                background: Premium[700],
                                                boxShadow: `0 0 10px 3px ${Premium[800]}`,
                                                animation: 'walk-strip 5s linear infinite',
                                                zIndex: 2,
                                                width: '80px',
                                                height: '80px',
                                            }}
                                        />

                                        <Box
                                            sx={{
                                                px: 2,
                                                py: 1,
                                                borderRadius: "6px",
                                                maxHeight: { xs: '7.5rem', sm: '12.7rem' },
                                                background: 'rgb(242, 237, 252)',
                                                overflow: 'auto',
                                                scrollbarWidth: 'none',
                                                '&::-webkit-scrollbar': { width: '6px' },
                                                '&::-webkit-scrollbar-thumb': {
                                                    backgroundColor: '#ccc',
                                                    borderRadius: '3px',
                                                },
                                                color: PremiumColor?.main,
                                                whiteSpace: 'pre-wrap',
                                                margin: '2px',
                                                zIndex: 3,
                                                width: 'calc(100% - 4px)',
                                                position: 'relative',
                                                boxSizing: 'border-box',
                                            }}
                                        >
                                            <Typography variant="body1">{insightDetails.rcaContent}</Typography>
                                        </Box>

                                  
                                    </Box>

                                </Box>
                            )}

                        </Box>
                    </Grid>


                    <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, }}>
                            {insightDetails?.attachmentIdDetails?.attachmentLink && (
                                <Box>
                                    <Typography variant="h6" sx={{ mb: 1 }}>
                                        Media File
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: 'grey.100',
                                            p: 2,
                                            borderRadius: 2,
                                            maxHeight: {
                                                sm: '15rem',
                                                md: '18rem',
                                                lg: 'unset',
                                            },
                                            border: '1px solid rgba(0,0,0,0.12)',
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={insightDetails.attachmentIdDetails.attachmentLink}
                                            alt="Insight Media"
                                            sx={{
                                                objectFit: 'cover',
                                                maxWidth: '100%',
                                                minHeight: '80px',
                                                height: {
                                                    xs: '5rem',
                                                    sm: '5.5rem',
                                                    md: '6.7rem',
                                                    lg: '7rem',
                                                },
                                            }}
                                        />
                                    </Box>
                                </Box>
                            )}

                            <Box sx={{marginTop : insightDetails?.attachmentIdDetails?.attachmentLink ? 1 : 0}}>
                                <Typography variant="h6">Comments</Typography>

                                <Box
                                    sx={{
                                        border: `1px solid ${Neutrals[200]}`,
                                        borderRadius: 2,
                                        p: 2,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 2,
                                        mt: 1,
                                    }}
                                >
                                    <Box

                                        ref={commentContainerRef}
                                        sx={{
                                            overflow: 'auto',
                                            px: 1,
                                            py: 1,
                                            height: insightDetails?.attachmentIdDetails?.attachmentLink
                                                ? {
                                                    xs: "10rem",
                                                    sm: "9.85rem",
                                                    md: "8.7rem",
                                                    lg: "8.3rem",
                                                    xl: "8.4rem",
                                                }
                                                : {
                                                    xs: "10rem",
                                                    sm: "7.2rem",
                                                    md: "9rem",
                                                    lg: "6.77rem",
                                                    xl: "13.2rem",
                                                },

                                            minHeight: insightDetails?.attachmentIdDetails?.attachmentLink ? "auto" : "300px",
                                            borderRadius: 2,
                                            scrollbarWidth: 'none',
                                            '&::-webkit-scrollbar': { width: '6px' },
                                            '&::-webkit-scrollbar-thumb': {
                                                backgroundColor: '#ccc',
                                                borderRadius: '3px',
                                            },
                                        }}
                                    >
                                        {comments?.length ? (
                                            comments.map((comment: any, index: number) => (
                                                <Box
                                                    key={index}
                                                    ref={index === comments.length - 1 ? lastElementRef : null}
                                                    sx={{
                                                        mb: 2,
                                                        p: 2,
                                                        bgcolor: '#fff',
                                                        border: '1px solid #ddd',
                                                        borderRadius: 1,
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Avatar
                                                                sx={{
                                                                    width: 26,
                                                                    height: 26,
                                                                    fontSize: 14,
                                                                    fontWeight: 800,
                                                                    bgcolor: '#fff',
                                                                    border: `2px solid ${Neutrals[300]}`,
                                                                    color: Neutrals[500],
                                                                }}
                                                            >
                                                                {comment?.senderDetails?.name
                                                                    ?.split(' ')
                                                                    .map((w: any) => w[0])
                                                                    .join('')
                                                                    .toUpperCase() || 'A'}
                                                            </Avatar>
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                                {comment?.senderDetails?.name || 'Anonymous'}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <AccessTimeIcon sx={{ color: Neutrals[500], fontSize: "18px" }} />
                                                            <Typography variant="caption" color="text.secondary">
                                                                {formatInsightDateTime(comment?.sentAt)}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                    <Typography variant="body2"
                                                        sx={{
                                                            overflow: 'auto',
                                                            scrollbarWidth: 'none',
                                                        }}>{comment?.comment}</Typography>
                                                </Box>
                                            ))
                                        ) : (
                                            <Typography variant="body2" sx={{ color: Neutrals[500] }}>
                                                No comments yet.
                                            </Typography>
                                        )}
                                    </Box>

                                    <Box sx={{ display: 'flex' }}>
                                        <InputBase
                                            multiline
                                            disabled={isPublic}
                                            placeholder="Write a comment..."
                                            value={newComment}
                                            onChange={e => setNewComment(e.target.value)}
                                            fullWidth
                                            endAdornment={
                                                <span title={isPublic ? "Login to comment" : ""}>
                                                    <IconButton onClick={handleCommentSubmit} disabled={isPublic}>
                                                        <SendIcon sx={{ color: isPublic ? Neutrals[500] : Primary[900], fontSize: '24px' }} />
                                                    </IconButton>
                                                </span>
                                            }
                                            sx={{
                                                px: 2,
                                                py: 1,
                                                border: '1px solid #ccc',
                                                borderRadius: '8px',
                                                fontSize: '14px',
                                                bgcolor: '#fff',
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>

            </Grid>
        </DialogContent>
    );
};

export default InsightDetailsContent;

